'use client'

import { useJobStore } from '@stores/job.store'

import dynamic from 'next/dynamic'

import type { JobPageItem, TermsConditions } from '@types'

import Button from '@components/buttons/button'
import ApplyDisclaimer from '@components/job/apply-disclaimer'
import withChat from '@hocs/withChat'
import useIsLoggedIn from '@hooks/user/useIsLoggedIn'

import styles from './apply-banner.module.scss'

import useApplyForAJob from '../hooks/useApplyForAJob'
import useLocale from '@hooks/client/useLocale'
import useApplyWithSonicApply from '../hooks/useApplyWithSonicApply'

const ReminderButton = dynamic(() => import('../reminder-button'), { ssr: false })
const ScrollApplyButton = dynamic(() => import('./scroll-apply-button'), { ssr: false })

type BannerType = {
  dataCy?: string
  isEligibleToApply: boolean
  isLinkedin?: boolean
  jobSource: JobPageItem['source']
  legals: TermsConditions[]
  scrollButton?: boolean
  totalInterestsShown?: number
}

function ApplyBanner({
  scrollButton,
  dataCy,
  legals,
  isLinkedin,
  totalInterestsShown,
  jobSource,
  isEligibleToApply,
}: BannerType): JSX.Element {
  const { isUserLoggedIn } = useIsLoggedIn()
  const isApplied = useJobStore((state) => state.isApplied)
  const locale = useLocale()

  const openChat = useApplyForAJob()
  const applyWithSonicApply = useApplyWithSonicApply()

  return (
    <>
      <div className={isApplied ? `${styles.apply} ${styles.applied}` : styles.apply}>
        <p className="h2">
          {totalInterestsShown && totalInterestsShown > 25
            ? 'Don’t miss out on this top job!'
            : 'Apply now in a few quick clicks'}
        </p>
        <Button
          onClick={locale === 'us' ? applyWithSonicApply : openChat}
          disabled={isApplied || !isEligibleToApply}
          dataCy={dataCy}
          className={styles['apply-btn']}
          variant="yellow"
        >
          {isApplied ? 'Applied!' : 'Apply now'}
        </Button>
        {!isApplied && isUserLoggedIn && (
          <span className={styles.reminder}>
            <ReminderButton />
          </span>
        )}
        <ApplyDisclaimer
          termsConditions={legals}
          jobSource={jobSource}
          isLinkedin={isLinkedin}
          isEligibleToApply={isEligibleToApply}
        />
      </div>
      {scrollButton && isEligibleToApply && (
        <ScrollApplyButton
          applyClick={locale === 'us' ? applyWithSonicApply : openChat}
          legals={legals}
          jobSource={jobSource}
          isEligibleToApply={isEligibleToApply}
        />
      )}
    </>
  )
}

export default withChat(ApplyBanner)
